import { useQuery } from '@tanstack/react-query';

import * as Api from 'contracts/api';
import { RequestAgent } from 'modules/request-agent';
import { useNotify } from 'pages/components/SnackBarProvider';
import { ExposedQueryFunctionContext, UseExposedQueryOptions } from 'queries/types';

export function useUserInfo<TData = Api.V4.User.Info.Response>(
    options?: UseExposedQueryOptions<Api.V4.User.Info.Response, Error, TData>
) {
    const notify = useNotify();
    return useQuery<Api.V4.User.Info.Response, Error, TData>({
        ...options,
        queryKey: Api.V4.User.Info.QueryKey(),
        queryFn: (ctx) =>
            fetchUserInfoQueryFn({
                ...ctx,
                onFailure: (error) => notify.openNotification({ level: 'error', message: error.message })
            })
    });
}

export async function fetchUserInfoQueryFn(context: ExposedQueryFunctionContext<Api.V4.User.Info.Response>) {
    const agent = context.agent || new RequestAgent();

    try {
        const response = await agent.public.get(Api.V4.User.Info.Endpoint(), { signal: context.signal });

        if (response.result === 'error') {
            return null;
        }

        await context?.onSuccess?.(response.result);
        return response.result;
    } catch (error) {
        await context?.onSuccess?.(error);
        throw error;
    }
}
